import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Chip,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    getFirestore, 
    collection, 
    getDocs, 
    query, 
    orderBy as firestoreOrderBy,
    doc,
    getDoc,
    runTransaction
} from 'firebase/firestore';

const firestore = getFirestore();

function EditTrabajo() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [servicios, setServicios] = useState([]);
    const [materiales, setMateriales] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const [formData, setFormData] = useState({
        fecha: moment(),
        cuadrilla: null,
        obra: null,
        observaciones: ''
    });
    
    const [serviciosSeleccionados, setServiciosSeleccionados] = useState([]);
    const [materialesSeleccionados, setMaterialesSeleccionados] = useState([]);
    const [materialesOriginales, setMaterialesOriginales] = useState([]);

    useEffect(() => {
        loadInitialData();
    }, [id]);

    const loadInitialData = async () => {
        try {
            setInitialLoading(true);
            await Promise.all([
                fetchTrabajo(),
                fetchServicios(),
                fetchMateriales()
            ]);
        } catch (error) {
            console.error('Error cargando datos:', error);
            showSnackbar('Error al cargar los datos', 'error');
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchTrabajo = async () => {
        const trabajoRef = doc(firestore, 'trabajos_huawei', id);
        const trabajoDoc = await getDoc(trabajoRef);
        
        if (!trabajoDoc.exists()) {
            showSnackbar('Trabajo no encontrado', 'error');
            setTimeout(() => navigate('/huawei/trabajos'), 2000);
            return;
        }

        const trabajoData = trabajoDoc.data();
        
        setFormData({
            fecha: moment(trabajoData.fecha, 'DD/MM/YYYY'),
            cuadrilla: trabajoData.cuadrilla,
            obra: trabajoData.obra,
            observaciones: trabajoData.observaciones || ''
        });

        setServiciosSeleccionados(trabajoData.servicios || []);
        setMaterialesSeleccionados(trabajoData.materiales || []);
        setMaterialesOriginales(trabajoData.materiales || []);
    };

    const fetchServicios = async () => {
        const q = query(collection(firestore, 'precios_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const serviciosData = [];
        querySnapshot.forEach((docSnap) => {
            serviciosData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setServicios(serviciosData);
    };

    const fetchMateriales = async () => {
        const q = query(collection(firestore, 'materiales_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const materialesData = [];
        querySnapshot.forEach((docSnap) => {
            materialesData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setMateriales(materialesData);
    };

    const handleServicioSelect = (event, newValue) => {
        if (newValue && !serviciosSeleccionados.find(s => s.idServicio === newValue.id)) {
            setServiciosSeleccionados([...serviciosSeleccionados, {
                ...newValue,
                idServicio: newValue.id,
                cantidad: 0
            }]);
        }
    };

    const handleServicioChange = (index, field, value) => {
        const updated = [...serviciosSeleccionados];
        updated[index][field] = value;
        setServiciosSeleccionados(updated);
    };

    const handleRemoveServicio = (index) => {
        setServiciosSeleccionados(serviciosSeleccionados.filter((_, i) => i !== index));
    };

    const handleMaterialSelect = (event, newValue) => {
        if (newValue && !materialesSeleccionados.find(m => m.idMaterial === newValue.id)) {
            setMaterialesSeleccionados([...materialesSeleccionados, {
                ...newValue,
                idMaterial: newValue.id,
                cantidad: 0
            }]);
        }
    };

    const handleMaterialChange = (index, field, value) => {
        const updated = [...materialesSeleccionados];
        updated[index][field] = value;
        setMaterialesSeleccionados(updated);
    };

    const handleRemoveMaterial = (index) => {
        setMaterialesSeleccionados(materialesSeleccionados.filter((_, i) => i !== index));
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const validateForm = () => {
        if (serviciosSeleccionados.length === 0 && materialesSeleccionados.length === 0) {
            showSnackbar('Debe tener al menos un servicio o material', 'warning');
            return false;
        }

        for (let i = 0; i < serviciosSeleccionados.length; i++) {
            const serv = serviciosSeleccionados[i];
            if (!serv.cantidad || serv.cantidad <= 0) {
                showSnackbar(`Debe ingresar una cantidad válida para ${serv.descripcion}`, 'warning');
                return false;
            }
        }

        for (let i = 0; i < materialesSeleccionados.length; i++) {
            const mat = materialesSeleccionados[i];
            if (!mat.cantidad || mat.cantidad <= 0) {
                showSnackbar(`Debe ingresar una cantidad válida para ${mat.descripcion}`, 'warning');
                return false;
            }
        }

        return true;
    };

    const calcularDiferenciasMateriales = () => {
        const diferencias = [];
        
        // Materiales originales que fueron removidos o reducidos
        materialesOriginales.forEach(materialOriginal => {
            const materialActual = materialesSeleccionados.find(m => m.idMaterial === materialOriginal.idMaterial);
            
            if (!materialActual) {
                // Material eliminado: devolver toda la cantidad al stock
                diferencias.push({
                    idMaterial: materialOriginal.idMaterial,
                    codigo: materialOriginal.codigo,
                    diferencia: materialOriginal.cantidad
                });
            } else if (materialActual.cantidad < materialOriginal.cantidad) {
                // Material reducido: devolver la diferencia al stock
                diferencias.push({
                    idMaterial: materialOriginal.idMaterial,
                    codigo: materialOriginal.codigo,
                    diferencia: materialOriginal.cantidad - parseFloat(materialActual.cantidad)
                });
            } else if (materialActual.cantidad > materialOriginal.cantidad) {
                // Material aumentado: restar la diferencia del stock
                diferencias.push({
                    idMaterial: materialOriginal.idMaterial,
                    codigo: materialOriginal.codigo,
                    diferencia: -(parseFloat(materialActual.cantidad) - materialOriginal.cantidad)
                });
            }
        });

        // Materiales nuevos agregados
        materialesSeleccionados.forEach(materialActual => {
            const materialOriginal = materialesOriginales.find(m => m.idMaterial === materialActual.idMaterial);
            
            if (!materialOriginal) {
                // Material nuevo: restar toda la cantidad del stock
                diferencias.push({
                    idMaterial: materialActual.idMaterial,
                    codigo: materialActual.codigo,
                    diferencia: -parseFloat(materialActual.cantidad)
                });
            }
        });

        return diferencias;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            const diferencias = calcularDiferenciasMateriales();

            await runTransaction(firestore, async (transaction) => {
                const trabajoRef = doc(firestore, 'trabajos_huawei', id);
                
                const trabajoData = {
                    fecha: formData.fecha.format('DD/MM/YYYY'),
                    timestamp: formData.fecha.valueOf(),
                    cuadrilla: formData.cuadrilla,
                    obra: formData.obra,
                    servicios: serviciosSeleccionados.map(s => ({
                        idServicio: s.idServicio || s.id,
                        codigo: s.codigo,
                        descripcion: s.descripcion,
                        unidad: s.unidad,
                        cantidad: parseFloat(s.cantidad),
                        zona1: s.zona1,
                        zona2: s.zona2,
                        zona3: s.zona3
                    })),
                    materiales: materialesSeleccionados.map(m => ({
                        idMaterial: m.idMaterial || m.id,
                        codigo: m.codigo,
                        descripcion: m.descripcion,
                        unidad: m.unidad,
                        cantidad: parseFloat(m.cantidad)
                    })),
                    observaciones: formData.observaciones,
                    updatedAt: moment().valueOf()
                };

                transaction.update(trabajoRef, trabajoData);

                // Actualizar stock según las diferencias
                for (const diferencia of diferencias) {
                    const stockQuery = query(
                        collection(firestore, 'materialesxcuadrilla_huawei')
                    );
                    const stockSnapshot = await getDocs(stockQuery);
                    
                    let stockDoc = null;
                    stockSnapshot.forEach((doc) => {
                        const data = doc.data();
                        if (data.idCuadrilla === formData.cuadrilla.docId && 
                            data.idMaterial === diferencia.idMaterial) {
                            stockDoc = { id: doc.id, ...data };
                        }
                    });

                    if (stockDoc) {
                        const stockRef = doc(firestore, 'materialesxcuadrilla_huawei', stockDoc.id);
                        const nuevoStock = (stockDoc.stock || 0) + diferencia.diferencia;
                        transaction.update(stockRef, { stock: nuevoStock });
                    } else if (diferencia.diferencia > 0) {
                        // Si no existe el stock y estamos devolviendo material, crear el registro
                        const newStockRef = doc(collection(firestore, 'materialesxcuadrilla_huawei'));
                        transaction.set(newStockRef, {
                            idCuadrilla: formData.cuadrilla.docId,
                            idMaterial: diferencia.idMaterial,
                            stock: diferencia.diferencia
                        });
                    }
                }
            });

            showSnackbar('Trabajo actualizado exitosamente', 'success');
            setTimeout(() => navigate('/huawei/trabajos'), 2000);
            
        } catch (error) {
            console.error('Error al actualizar trabajo:', error);
            showSnackbar('Error al actualizar el trabajo', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton onClick={() => navigate('/huawei/trabajos')} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1">
                        Editar Trabajo Ejecutado - Huawei
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Solo puedes editar los servicios y materiales. La fecha, cuadrilla y obra no pueden modificarse.
                    Los cambios en materiales actualizarán automáticamente el stock.
                </Alert>

                <Paper sx={{ p: 3, mt: 3 }}>
                    <Grid container spacing={3}>
                        {/* Campos deshabilitados */}
                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Fecha"
                                    value={formData.fecha}
                                    disabled
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    format="DD/MM/YYYY"
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Cuadrilla"
                                value={formData.cuadrilla?.numero || ''}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Obra"
                                value={formData.obra?.nombre || ''}
                                disabled
                            />
                        </Grid>

                        {/* Observaciones */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Observaciones"
                                name="observaciones"
                                value={formData.observaciones}
                                onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Servicios Realizados
                            </Typography>
                        </Grid>

                        {/* Autocomplete de Servicios */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={servicios}
                                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                                onChange={handleServicioSelect}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Agregar Servicio"
                                        placeholder="Buscar por código o descripción"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Lista de servicios seleccionados */}
                        {serviciosSeleccionados.length > 0 && (
                            <Grid item xs={12}>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Cantidad *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 80 }}>Acción</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {serviciosSeleccionados.map((servicio, index) => (
                                                <TableRow key={`${servicio.idServicio || servicio.id}-${index}`}>
                                                    <TableCell>{servicio.codigo}</TableCell>
                                                    <TableCell>{servicio.descripcion}</TableCell>
                                                    <TableCell>
                                                        <Chip label={servicio.unidad} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={servicio.cantidad}
                                                            onChange={(e) => handleServicioChange(index, 'cantidad', e.target.value)}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRemoveServicio(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Materiales Utilizados
                            </Typography>
                        </Grid>

                        {/* Autocomplete de Materiales */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={materiales}
                                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                                onChange={handleMaterialSelect}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Agregar Material"
                                        placeholder="Buscar por código o descripción"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Lista de materiales seleccionados */}
                        {materialesSeleccionados.length > 0 && (
                            <Grid item xs={12}>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Cantidad *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 80 }}>Acción</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {materialesSeleccionados.map((material, index) => (
                                                <TableRow key={`${material.idMaterial || material.id}-${index}`}>
                                                    <TableCell>{material.codigo}</TableCell>
                                                    <TableCell>{material.descripcion}</TableCell>
                                                    <TableCell>
                                                        <Chip label={material.unidad} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={material.cantidad}
                                                            onChange={(e) => handleMaterialChange(index, 'cantidad', e.target.value)}
                                                            inputProps={{ min: 0, step: 1 }}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRemoveMaterial(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {/* Botones de acción */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/huawei/trabajos')}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                                >
                                    {loading ? 'Guardando...' : 'Actualizar Trabajo'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default EditTrabajo;
